<template>
  <div class="forum-search-wrapper">
    <!-- Search Form -->
    <div class="search-form" @keyup.enter="performSearch">
      <input v-model="keyword" type="text" placeholder="Buscar en el foro…" class="search-input" />

      <select v-model="selectedSection" class="search-select">
        <option value="">Todas las secciones</option>
        <option v-for="section in sections" :value="section.tid" :key="section.tid">
          {{ section.name }}
        </option>
      </select>

      <label class="search-checkbox"><input type="checkbox" v-model="includeTopics" /> Temas</label>
      <label class="search-checkbox"><input type="checkbox" v-model="includeComments" /> Comentarios</label>
      <label class="search-checkbox"><input type="checkbox" v-model="exact" /> Coincidencia exacta</label>

      <button @click="performSearch" class="search-button">Buscar</button>
    </div>

    <!-- Results -->
    <div class="search-results" v-if="resultsReady">
      <p v-if="loading" class="loading-message typo-body-2">Buscando…</p>
      <p v-if="error" class="error-message typo-body-2">{{ error }}</p>

      <div v-if="!loading && !error">
        <!-- Topics -->
        <div v-if="topics.length">
          <h3>Temas encontrados ({{ totalTopics }})</h3>
          <ul class="results-list">
            <li v-for="topic in topics" :key="topic.nid" class="result-item">
              <router-link :to="{ name: 'ForumThread', params: { nid: topic.nid } }" class="result-link">
                {{ topic.title }}
              </router-link>
              <span class="result-meta">{{ formatDate(topic.created) }}</span>
            </li>
          </ul>
        </div>

        <!-- Comments -->
        <div v-if="comments.length">
          <h3>Comentarios encontrados ({{ totalComments }})</h3>
          <ul class="results-list">
            <li v-for="comment in comments" :key="comment.cid" class="result-item">
              <router-link :to="{ name: 'ForumThread', params: { nid: comment.nid }, hash: '#comment-' + comment.cid }" class="result-link">
                {{ comment.subject || '[Sin asunto]' }}
              </router-link>
              <span class="result-meta">{{ formatDate(comment.created) }}</span>
            </li>
          </ul>
        </div>

        <div v-if="!topics.length && !comments.length" class="no-results typo-body-2">No se encontraron resultados.</div>
      </div>

      <!-- Pagination -->
      <div v-if="showPagination" class="pagination">
        <button :disabled="currentPage === 0" @click="prevPage">Anterior</button>
        <span>Página {{ currentPage + 1 }}</span>
        <button :disabled="!hasMoreResults" @click="nextPage">Siguiente</button>
      </div>
    </div>
  </div>
</template>

<script>
import forumApi from '@/services/forumApi';
const axios = forumApi;
export default {
  name: 'ForumSearch',
  data() {
    return {
      keyword: '',
      sections: [],
      selectedSection: '',
      includeTopics: true,
      includeComments: true,
      exact: false,
      loading: false,
      error: null,
      topics: [],
      comments: [],
      totalTopics: 0,
      totalComments: 0,
      currentPage: 0,
      showPagination: false,
    };
  },
  computed: {
    resultsReady() {
      return this.loading || this.error !== null || this.topics.length || this.comments.length;
    },
    hasMoreResults() {
      const currentBatchSize = this.topics.length + this.comments.length;
      return currentBatchSize === 10;
    },
  },
  mounted() {
    this.fetchSections();
  },
  methods: {
    formatDate(ts) {
      const d = new Date(ts * 1000);
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
    },
    fetchSections() {
      axios
        .get('/forum/section-ids')
        .then((res) => {
          if (Array.isArray(res.data)) {
            this.sections = res.data;
          }
        })
        .catch((err) => console.error('Error fetching sections', err));
    },
    performSearch() {
      if (!this.keyword) return;
      this.loading = true;
      this.error = null;
      const payload = {
        keyword: this.keyword,
        id: this.selectedSection,
        exact: this.exact.toString(),
        page: this.currentPage,
        topics: this.includeTopics.toString(),
        comments: this.includeComments.toString(),
      };
      const formData = new URLSearchParams();
      Object.entries(payload).forEach(([k, v]) => formData.append(k, v));
      axios
        .post('/forum/api/search', formData, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } })
        .then((res) => {
          const data = res.data || {};
          this.topics = Array.isArray(data.topics) ? data.topics : [];
          this.comments = Array.isArray(data.comments) ? data.comments : [];
          this.totalTopics = data.totalTopics || 0;
          this.totalComments = data.totalComments || 0;
          this.showPagination = this.totalTopics + this.totalComments > 10;
        })
        .catch((err) => {
          console.error('Search error', err);
          this.error = 'Error al buscar.';
        })
        .finally(() => {
          this.loading = false;
        });
    },
    nextPage() {
      this.currentPage += 1;
      this.performSearch();
    },
    prevPage() {
      if (this.currentPage > 0) {
        this.currentPage -= 1;
        this.performSearch();
      }
    },
  },
};
</script>

<style scoped>
.forum-search-wrapper {
  background-color: var(--color-surface);
  border-radius: 15px;
  padding: 20px;
}
.search-form {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}
.search-input {
  flex: 1 1 250px;
  padding: 8px 12px;
  border: 1px solid var(--color-outline);
  border-radius: 4px;
}
.search-select {
  flex: 0 1 180px;
  padding: 8px 12px;
  border: 1px solid var(--color-outline);
  border-radius: 4px;
}
.search-checkbox {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.875rem;
}
.search-button {
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, #fff);
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}
.search-button:hover {
  background-color: var(--color-highlight-dark, #1976d2);
}
.search-results {
  margin-top: 16px;
}
.results-list {
  list-style: none;
  padding: 0;
}
.result-item {
  padding: 6px 0;
  border-bottom: 1px solid var(--color-outline);
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.result-link {
  color: var(--color-highlight);
  text-decoration: none;
}
.result-meta {
  font-size: 0.75rem;
  color: var(--color-text-secondary);
}
.pagination {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 16px;
}
.pagination button {
  padding: 6px 12px;
  border: 1px solid var(--color-outline);
  background-color: var(--color-surface);
  cursor: pointer;
}
.pagination button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.loading-message,
.error-message,
.no-results {
  text-align: center;
  margin: 20px 0;
}
</style> 